import { useState, useCallback } from 'react'
import type { GridData, PlacedWord, Difficulty, SearchMode } from './WordSearchData'
import { generateGrid } from './WordSearchData'
import type { GameItem } from '../../i18n'

export type { Difficulty, SearchMode }

export interface Cell {
  row: number
  col: number
}

export interface FoundWord {
  word:       string   // original word for display
  normalized: string
  cells:      Cell[]
}

export interface WordSearchState {
  grid:         GridData
  selection:    Cell[]
  foundWords:   FoundWord[]
  moves:        number
  startTime:    number | null
  endTime:      number | null
  gameComplete: boolean
}

function initState(difficulty: Difficulty, mode: SearchMode, items: GameItem[]): WordSearchState {
  return {
    grid:         generateGrid(items.map(i => i.word), difficulty, mode),
    selection:    [],
    foundWords:   [],
    moves:        0,
    startTime:    null,
    endTime:      null,
    gameComplete: false,
  }
}

// Returns true if `next` is a valid continuation of `sel`:
// adjacent to the last cell and consistent with the established direction.
function isConsistentNext(sel: Cell[], next: Cell): boolean {
  if (sel.length === 0) return true

  const last = sel[sel.length - 1]
  const dr = next.row - last.row
  const dc = next.col - last.col

  // Must be exactly one step (including diagonals)
  if (Math.abs(dr) > 1 || Math.abs(dc) > 1 || (dr === 0 && dc === 0)) return false

  // First extension — any adjacent cell is fine
  if (sel.length === 1) return true

  // Must match the direction established by the first two cells
  const estDr = sel[1].row - sel[0].row
  const estDc = sel[1].col - sel[0].col
  return dr === estDr && dc === estDc
}

function findPlacedMatch(
  sel: Cell[],
  placedWords: PlacedWord[],
  cells: string[][],
): PlacedWord | null {
  if (sel.length < 2) return null

  const dr      = sel[1].row - sel[0].row
  const dc      = sel[1].col - sel[0].col
  const selWord = sel.map(c => cells[c.row][c.col]).join('')

  return placedWords.find(pw => {
    if (pw.normalized.length !== sel.length) return false

    // Forward: user selected in the same direction the word was placed
    if (
      pw.normalized === selWord &&
      pw.row === sel[0].row &&
      pw.col === sel[0].col &&
      pw.dr  === dr &&
      pw.dc  === dc
    ) return true

    // Reverse: user selected the word backwards (e.g. tapped N-O-E-L for LEON)
    const reversed = pw.normalized.split('').reverse().join('')
    const endRow   = pw.row + pw.dr * (pw.normalized.length - 1)
    const endCol   = pw.col + pw.dc * (pw.normalized.length - 1)
    return (
      reversed  === selWord &&
      endRow    === sel[0].row &&
      endCol    === sel[0].col &&
      -pw.dr    === dr &&
      -pw.dc    === dc
    )
  }) ?? null
}

function applyTap(prev: WordSearchState, row: number, col: number): WordSearchState {
  const { grid, selection, foundWords } = prev
  const newMoves  = prev.moves + 1
  const startTime = prev.startTime ?? Date.now()
  const tapped: Cell = { row, col }

  // Tapping a cell already in the current selection clears it
  const alreadyInSel = selection.some(c => c.row === row && c.col === col)
  if (alreadyInSel) {
    return { ...prev, selection: [], moves: newMoves, startTime }
  }

  const newSel: Cell[] = isConsistentNext(selection, tapped)
    ? [...selection, tapped]
    : [tapped]

  const match = findPlacedMatch(newSel, grid.placedWords, grid.cells)
  const alreadyFound = match
    ? foundWords.some(fw => fw.normalized === match.normalized)
    : false

  if (match && !alreadyFound) {
    const newFoundWords: FoundWord[] = [
      ...foundWords,
      { word: match.word, normalized: match.normalized, cells: newSel },
    ]
    const gameComplete = newFoundWords.length === grid.placedWords.length
    return {
      ...prev,
      selection:    [],
      foundWords:   newFoundWords,
      moves:        newMoves,
      startTime,
      gameComplete,
      endTime: gameComplete ? Date.now() : prev.endTime,
    }
  }

  return { ...prev, selection: newSel, moves: newMoves, startTime }
}

export function useWordSearch(difficulty: Difficulty, mode: SearchMode, items: GameItem[]) {
  const [state, setState] = useState<WordSearchState>(() => initState(difficulty, mode, items))

  const resetGame = useCallback(() => {
    setState(initState(difficulty, mode, items))
  }, [difficulty, mode, items])

  // Tap mode: single-cell tap that builds a chain and auto-finds on match
  const tapCell = useCallback((row: number, col: number) => {
    setState(prev => applyTap(prev, row, col))
  }, [])

  // Drag mode — step 1: reset selection to a single cell (no moves/startTime change)
  const beginSelection = useCallback((row: number, col: number) => {
    setState(prev => ({ ...prev, selection: [{ row, col }] }))
  }, [])

  // Drag mode — step 2: extend selection during drag; silently ignores inconsistent cells
  const extendDragSelection = useCallback((row: number, col: number) => {
    setState(prev => {
      const { selection } = prev
      const cell: Cell = { row, col }
      if (selection.some(c => c.row === row && c.col === col)) return prev
      if (!isConsistentNext(selection, cell)) return prev
      return { ...prev, selection: [...selection, cell] }
    })
  }, [])

  // Drag mode — step 3: commit on release; checks for a word match then clears selection
  const commitSelection = useCallback(() => {
    setState(prev => {
      const { grid, selection, foundWords } = prev
      const startTime = prev.startTime ?? Date.now()
      const newMoves  = prev.moves + 1
      if (selection.length < 2) return { ...prev, selection: [], moves: newMoves, startTime }

      const match       = findPlacedMatch(selection, grid.placedWords, grid.cells)
      const alreadyFound = match ? foundWords.some(fw => fw.normalized === match.normalized) : false

      if (match && !alreadyFound) {
        const newFoundWords: FoundWord[] = [
          ...foundWords,
          { word: match.word, normalized: match.normalized, cells: selection },
        ]
        const gameComplete = newFoundWords.length === grid.placedWords.length
        return {
          ...prev,
          selection:    [],
          foundWords:   newFoundWords,
          moves:        newMoves,
          startTime,
          gameComplete,
          endTime: gameComplete ? Date.now() : prev.endTime,
        }
      }
      return { ...prev, selection: [], moves: newMoves, startTime }
    })
  }, [])

  return { state, resetGame, tapCell, beginSelection, extendDragSelection, commitSelection }
}
